import dayjs, { Dayjs } from 'dayjs'
import React, { useState } from 'react'
import { MonthNames } from '../../../Widgets/AgTechDateHeader'
import { useAgTechPopUpContext } from '@agtech/core/functions/AgTechAppPopUps'

export type AgTechDatePickerProps = {
    callerRef: React.RefObject<HTMLElement>,
    dateChangedHandlerRef: React.MutableRefObject<AgTechDatePickerChangeHandler>,
    initialDateValue?: string,
    onDateSelected: (date: string) => void,
    settings?: {
        classes?: string
    }
}

export type AgTechDatePickerChangeHandler = {
    onDateChanged: (date: string) => void
}

export const DayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const AgTechDatePicker = (props: AgTechDatePickerProps) => {
    const popupContext = useAgTechPopUpContext();

    const [pivotDate, setPivotDate] = useState(props.initialDateValue ? dayjs(props.initialDateValue) : dayjs());

    const today = dayjs();
    let currentDate = pivotDate;

    if (!currentDate.isValid()) {
        currentDate = today;
    }
    else if (currentDate.year() === 2001 && !props.initialDateValue?.includes('2001')) {
        // DayJS defaults the date to 2001 if not specified for no apparent reason, so
        // default it to the current date like a sane person would expect
        currentDate = currentDate.set('year', today.year());
    }

    props.dateChangedHandlerRef.current = {
        onDateChanged: (date: string) => {
            setPivotDate(dayjs(date));
        }
    }

    const handleDateSelected = (date: Dayjs) => {
        props.onDateSelected(date.format('MM-DD-YYYY'));
        popupContext.closePopUp();
    }

    const priorMonth = currentDate.add(-1, 'month');
    const lastDayInPriorMonth = priorMonth.endOf('month');

    const firstDayInMonth = currentDate.startOf('month');
    const lastDayInMonth = currentDate.endOf('month');

    const firstDayInMonthDayOfWeek = firstDayInMonth.day();
    const lastDayInMonthDayOfWeek = lastDayInMonth.day();

    const daysInMonth = currentDate.daysInMonth();

    const dates: {
        date: Dayjs,
        isCurrentMonth: boolean
    }[] = [];

    for (let i = firstDayInMonthDayOfWeek - 1; i >= 0; i--) {
        dates.push({
            date: lastDayInPriorMonth.add(-i, 'day'),
            isCurrentMonth: false
        });
    }

    for (let i = 1; i <= daysInMonth; i++) {
        dates.push({
            date: firstDayInMonth.add(i - 1, 'day'),
            isCurrentMonth: true
        });
    }

    for (let i = 1; i < 7 - lastDayInMonthDayOfWeek; i++) {
        dates.push({
            date: lastDayInMonth.add(i, 'day'),
            isCurrentMonth: false
        });
    }

    if (dates.length < 42) {
        const lastDate = dates[dates.length - 1]?.date;

        if (lastDate) {
            for (let i = 1; i <= 7; i++) {
                dates.push({
                    date: lastDate.add(i, 'day'),
                    isCurrentMonth: false
                });
            }
        }
    }

    const weeksInMonth = [0, 1, 2, 3, 4, 5];
    const daysInWeek = [0, 1, 2, 3, 4, 5, 6];

    return (
        <div className='row-based h-[248px] w-[248px] rounded bordered bg-white p-3 flex'>
            <div className='column-based wide justify-evenly py-1 mb-2 flex'>
                <i
                    className='bi bi-chevron-left text-base mr-auto clickable color-changing hover:text-highlight'
                    onClick={() => setPivotDate(curr => curr.add(-1, 'month'))}
                />
                <h1 className='font-semibold mx-auto text-base flex'>
                    {MonthNames[currentDate.month()] + ', ' + currentDate.year()}
                </h1>
                <i
                    className='bi bi-chevron-right text-base ml-auto clickable color-changing hover:text-highlight'
                    onClick={() => setPivotDate(curr => curr.add(1, 'month'))}
                />
            </div>
            <div className='row-based flexed mt-1 flex'>
                <div className='column-based wide justify-evenly flex'>
                    {DayNames.map((day, index) => (
                        <div key={index} className='text-sm font-semibold text-detail centered flexed flex'>
                            {day}
                        </div>
                    ))}
                </div>
                <div className='row-based wide pt-2 flex-wrap flex'>
                    {weeksInMonth.map((week, index) => {
                        return (
                            <div key={index} className='column-based wide justify-evenly flexed py-1 flex'>
                                {daysInWeek.map((day, index) => {
                                    const date = dates[week * 7 + day];
                                    const classes = date?.isCurrentMonth ? '' : 'text-slate-300';

                                    const isTodayClasses = date?.date.isSame(today, 'day') ? 'text-highlight' : '';
                                    const isSelectedClasses = date?.date?.isSame(currentDate, 'day') ? 'bg-highlight text-white' : '';

                                    return (
                                        <div
                                            key={index}
                                            className={`font-semibold text-sm clickable rounded-md centered flexed flex ${classes} ${isTodayClasses} ${isSelectedClasses} hover:bg-highlight-100`}
                                            onClick={() => handleDateSelected(date?.date)}
                                        >
                                            {date?.date?.date()}
                                        </div>
                                    )
                                })}
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default AgTechDatePicker